import { Injectable } from "@angular/core";
import { ADD_CODES, REMOVE_CODES } from "@shared/misc";
import { HssLeg } from "./models/hss-leg";
import { DateTime, Duration } from "luxon";
import { Hss } from "./models/hss";
import { HssItem } from "./models/hss-item";
import { HssDutyPeriod } from "./models/hss-duty-period";
import { RapItem } from "./models/history";

// Shared functions used by HssBuilder and child components.
@Injectable({ providedIn: "root" })
export class HssBuilderService {
  readonly DATE_FORMAT = "LL/dd/yyyy HH:mm:ss";

  // Remove leading zeroes
  toInt(text: string): string {
    return parseInt(text).toString();
  }

  getCodeDescription(code: string, addOrRemove: "add" | "remove"): string {
    if (addOrRemove === "add") {
      return ADD_CODES[code.trim()] || "Unknown";
    } else {
      return REMOVE_CODES[code.trim()] || "Unknown";
    }
  }

  // Determine whether RSK row should display.
  // If actual dep time exists and RSK dep time is different than SKD dep time then RSK should display.
  shouldShowRsk(leg: HssLeg): boolean {
    if (leg.isDepTimeReSched.text === "False") {
      return false;
    }

    if (leg.isDepTimeActual.text === "False") {
      return true;
    }

    if (leg.depTimeIsEstimated.text === "True") {
      return true;
    }

    if (leg.depTimeSched.text !== leg.depTimeReSched.text) {
      return true;
    }

    return false;
  }

  isFaded(leg: HssLeg, status: "skd" | "rsk"): boolean {
    if (
      status === "skd" &&
      (this.shouldShowRsk(leg) || leg.isDepTimeActual.text === "True")
    ) {
      return true;
    }

    return status === "rsk" && leg.isDepTimeActual.text == "True";
  }

  getDay(leg: HssLeg, type: "SKD" | "RSK" | "ACT"): string {
    switch (type) {
      case "SKD":
        if (leg.depTimeSchedLocal.text.length === 19) {
          return leg.depTimeSchedLocal.text.substring(3, 5);
        }
        break;
      case "RSK":
        if (leg.depTimeReSchedLocal.text.length === 19) {
          return leg.depTimeReSchedLocal.text.substring(3, 5);
        }
        break;
      case "ACT":
        if (leg.depTimeActualLocal.text.length === 19) {
          return leg.depTimeActualLocal.text.substring(3, 5);
        }
        break;
    }

    return "";
  }

  // Updates changed class to make sure animation triggers when changing updates.
  getChangedClass(didChange: boolean, index?: number): string {
    if (didChange && index !== undefined) {
      return `changed-${index % 2}`;
    }
    return "";
  }

  getFlightTimeSched(leg: HssLeg, ommitDhCode = false): string {
    const dhCode = this.getDhCode(leg);

    // Report always shows 0.00 fly
    if (dhCode === "RPT") {
      return " 0.00RPRT";
    }

    return `${this.getFlightTime(
      leg.depTimeSched.text,
      leg.arrTimeSched.text,
      leg,
    )}${ommitDhCode ? "" : dhCode || "  "}`;
  }

  getFlightTimeReSched(leg: HssLeg): string {
    const dhCode = this.getDhCode(leg);
    return `${this.getFlightTime(
      leg.depTimeReSched.text,
      leg.arrTimeReSched.text,
      leg,
    )}${dhCode || "  "}`;
  }

  getFlightTimeActual(leg: HssLeg, ommitDhCode = false): string {
    const dhCode = this.getDhCode(leg);
    return `${this.getFlightTime(
      leg.depTimeActual.text,
      leg.arrTimeActual.text,
      leg,
      true,
    )}${ommitDhCode ? "" : dhCode || "  "}`;
  }

  // CBA rest time uses dep time actual and arr time sched.
  getCbaRestTime(leg: HssLeg): string {
    return `${this.getFlightTime(
      leg.depTimeActual.text,
      leg.arrTimeSched.text,
      leg,
      true,
    )}`;
  }

  // Show seat actual if seatActual differs from sequence seat
  getSeatActual(leg: HssLeg, hss: Hss): string {
    if (
      leg.seatActual.text !== "" &&
      leg.seatActual.text.toUpperCase() !== hss.header.position.toUpperCase()
    ) {
      return leg.seatActual.text;
    } else {
      return "  ";
    }
  }

  getSkdEquipCode(leg: HssLeg): string {
    // Use actual are sched equip code in that priority
    if (leg.actualEquipCode.text !== "") {
      return leg.actualEquipCode.text;
    }

    return leg.schedEquipCode.text;
  }

  // Returns 'SKD', 'EST, or 'GTR' depending on duty period state
  getDpState(hss: Hss, dpIndex: number): string {
    // Note: these can be the same leg for duty periods with one leg.
    let dpStartLeg: HssLeg | undefined = undefined;
    let dpEndLeg: HssLeg | undefined = undefined;

    // Special case: No active legs (DH only), but DH is non-AA, return GTR.
    let noActive = true;
    let oalDh = false;

    for (const leg of hss.legs) {
      if (leg.dpIndex === dpIndex && leg.isDpStart.text === "True") {
        dpStartLeg = leg;
      }

      if (leg.dpIndex === dpIndex && leg.isDpEnd.text === "True") {
        dpEndLeg = leg;
      }

      if (leg.dpIndex === dpIndex && leg.isActive.text === "True") {
        noActive = false;

        if (
          leg.isDhOrSurface.text === "True" &&
          leg.oalSurfaceAirline.text !== "AA"
        ) {
          oalDh = true;
        }
      }
    }

    if (oalDh) {
      return "GTR";
    }

    if (
      dpEndLeg?.arrTimeActualLocal.text !== "" &&
      dpEndLeg?.arrTimeIsEstimated.text === "False"
    ) {
      return "GTR";
    }

    if (dpEndLeg?.arrTimeIsEstimated.text === "True") {
      return "EST";
    }

    if (
      dpStartLeg?.depTimeActualLocal.text === "" ||
      dpStartLeg?.depTimeIsEstimated.text === "True"
    ) {
      return "SKD";
    }

    return "EST";
  }

  // Add greater + P/C to get duty period total
  getDpTotal(dp: HssDutyPeriod): string {
    const payCredit = this.getDurationFromString(dp.payCreditDisplayed.text);
    const greater = this.getDurationFromString(dp.greaterTime.text);
    return payCredit.plus(greater).toFormat("h.mm");
  }

  getSchedOnDuty(dp: HssDutyPeriod): HssItem {
    const hssItem = new HssItem(null);

    const dutyStart = DateTime.fromFormat(
      dp.sched121OnDutyStartTime.text,
      this.DATE_FORMAT,
    );
    const dutyEnd = DateTime.fromFormat(
      dp.sched121OnDutyEndTime.text,
      this.DATE_FORMAT,
    );
    const onDuty = dutyEnd.diff(dutyStart).toFormat("h.mm");

    hssItem.didChange =
      dp.sched121OnDutyStartTime.didChange ||
      dp.sched121OnDutyEndTime.didChange;
    hssItem.text = onDuty;

    return hssItem;
  }

  // Check legs for SeqEnd flag.
  isLastDp(hss: Hss, dpIndex: number): boolean {
    for (const leg of hss.legs) {
      if (leg.isSeqEnd.text === "True" && leg.dpIndex === dpIndex) {
        return true;
      }
    }
    return false;
  }

  getSchedOdl(hss: Hss, dpIndexA: number, dpIndexB: number): HssItem {
    const hssItem = new HssItem(null);

    // Need to decrement indices since provided ones aren't zero based.
    dpIndexA--;
    dpIndexB--;

    if (
      dpIndexA >= hss.dutyPeriods.length ||
      dpIndexB >= hss.dutyPeriods.length
    ) {
      return hssItem;
    }

    const dutyPeriodAEndTime = DateTime.fromFormat(
      hss.dutyPeriods[dpIndexA].sched121OnDutyEndTime.text,
      this.DATE_FORMAT,
    );
    const dutyPeriodBStartTime = DateTime.fromFormat(
      hss.dutyPeriods[dpIndexB].sched121OnDutyStartTime.text,
      this.DATE_FORMAT,
    );
    const odlTime = dutyPeriodBStartTime
      .diff(dutyPeriodAEndTime)
      .toFormat("h.mm");

    hssItem.text = odlTime;
    hssItem.didChange =
      hss.dutyPeriods[dpIndexA].sched121OnDutyEndTime.didChange ||
      hss.dutyPeriods[dpIndexB].sched121OnDutyStartTime.didChange;

    return hssItem;
  }

  getActualOnDuty(dp: HssDutyPeriod): HssItem {
    const hssItem = new HssItem(null);

    const dutyStart = DateTime.fromFormat(dp.startTime.text, this.DATE_FORMAT);
    const dutyEnd = DateTime.fromFormat(dp.endTime.text, this.DATE_FORMAT);
    const onDuty = dutyEnd.diff(dutyStart).toFormat("h.mm");

    hssItem.didChange = dp.startTime.didChange || dp.endTime.didChange;
    hssItem.text = onDuty;

    return hssItem;
  }

  getActualOdl(hss: Hss, dpIndexA: number, dpIndexB: number): HssItem {
    const hssItem = new HssItem(null);

    // Need to decrement indices since provided ones aren't zero based.
    dpIndexA--;
    dpIndexB--;

    if (
      dpIndexA >= hss.dutyPeriods.length ||
      dpIndexB >= hss.dutyPeriods.length
    ) {
      return hssItem;
    }

    const dutyPeriodAEndTime = DateTime.fromFormat(
      hss.dutyPeriods[dpIndexA].endTime.text,
      this.DATE_FORMAT,
    );
    const dutyPeriodBStartTime = DateTime.fromFormat(
      hss.dutyPeriods[dpIndexB].startTime.text,
      this.DATE_FORMAT,
    );
    const odlTime = dutyPeriodBStartTime
      .diff(dutyPeriodAEndTime)
      .toFormat("h.mm");

    hssItem.text = odlTime;
    hssItem.didChange =
      hss.dutyPeriods[dpIndexA].endTime.didChange ||
      hss.dutyPeriods[dpIndexB].startTime.didChange;

    return hssItem;
  }

  // Determines whether sign-in time should be shown (SI XXXX/XX).
  // If the duty period start time differs
  // from the normal sign-in time (60 or 30 min prior).
  // Returns sign-in text or empty string.
  getSignInTimeText(
    hss: Hss,
    dp: HssDutyPeriod,
    stationTimeZones: Record<string, string>,
  ): string {
    // console.log(hss, dp.index);
    for (const leg of hss.legs) {
      // Automatically show sign-in time if first leg of dp was cancelled or report.
      if (
        leg.dpIndex === dp.index &&
        (leg.isCancelled.text === "True" ||
          leg.oalSurfaceAirline.text === "RPT")
      ) {
        // Determine sign-in time.
        let signInMinutes = 60;
        if (leg.oalSurfaceAirline.text === "RPT") {
          signInMinutes = 0;
        } else if (leg.isDhOrSurface.text === "True") {
          signInMinutes = 30;
        }

        let depTime = leg.depTimeSchedLocal.text;
        if (leg.isDepTimeReSched.text === "True") {
          depTime = leg.depTimeReSchedLocal.text;
        }

        const depStation = leg.depStation.text;

        if (stationTimeZones && stationTimeZones[depStation]) {
          const signInTime = DateTime.fromFormat(
            dp.fdpStart.text,
            this.DATE_FORMAT,
            { zone: "UTC" },
          ).setZone(stationTimeZones[depStation]);
          return `SI ${signInTime.toFormat("HHmm/dd")}`;
        } else {
          return "";
        }
      }

      if (leg.isActive.text === "True" && leg.dpIndex === dp.index) {
        // Determine sign-in time.
        let signInMinutes = 60;
        if (leg.oalSurfaceAirline.text === "RPT") {
          signInMinutes = 0;
        } else if (leg.isDhOrSurface.text === "True") {
          signInMinutes = 30;
        }

        let depTime = leg.depTimeSched.text;

        let calculatedSignInTime = DateTime.fromFormat(
          depTime,
          this.DATE_FORMAT,
        ).minus({ minutes: signInMinutes });
        let fdpStartTime = DateTime.fromFormat(
          dp.fdpStart.text,
          this.DATE_FORMAT,
        );

        if (
          calculatedSignInTime.toFormat("HH:mm") !==
          fdpStartTime.toFormat("HH:mm")
        ) {
          const depStation = leg.depStation.text;

          if (stationTimeZones && stationTimeZones[depStation]) {
            const signInTime = DateTime.fromFormat(
              dp.fdpStart.text,
              this.DATE_FORMAT,
              { zone: "UTC" },
            ).setZone(stationTimeZones[depStation]);
            return `SI ${signInTime.toFormat("HHmm/dd")}`;
          }
        } else {
          return "";
        }
      }
    }
    return "";
  }

  timeFromMinutes(minutes: string): string {
    if (minutes === "") {
      return "";
    }

    const minutesInt = parseInt(minutes);
    const hours = Math.floor(minutesInt / 60);
    const remainingMinutes = minutesInt % 60;

    return `${hours}.${remainingMinutes.toString().padStart(2, "0")}`;
  }

  // Returns HSS RAP text: RSV FDP BEGAN XXXX/XX ENDS XX/XXXX
  // or null if N/A.
  getRapText(
    empNum: string,
    dp: HssDutyPeriod,
    raps: RapItem[],
  ): string | null {
    for (const rap of raps || []) {
      if (empNum === rap.empNum) {
        // Compare days. The day value is all that's needed.
        // dp.fdpStartAcclim uses MM/dd/yyyy format.
        // RAP info will only apply to first duty period,
        // so using acclimated FDP start time works fine.
        const dpDay = dp.fdpStartAcclim.text.substring(3, 5);
        // RAP uses yyyy-MM-dd format.
        const rapDay = rap.rapStartTime.substring(8, 10);

        if (dpDay === rapDay) {
          const rapStartDateTime = DateTime.fromISO(rap.rapStartTime);
          let rapEndDateTime = rapStartDateTime.plus({ hours: 16 });

          // I don't why this is 19 hours. But it matches Sabre HSS.
          if (dp.isAugmented.text === "81") {
            rapEndDateTime = rapStartDateTime.plus({ hours: 19 });
          }

          const rapStartTime = rapStartDateTime.toFormat("HHmm/dd");
          const rapEndTime = rapEndDateTime.toFormat("HHmm/dd");
          return `RSV FDP BEGAN ${rapStartTime} ENDS ${rapEndTime}`;
        }
      }
    }
    return null;
  }

  // Returns GTR, SKD, or EST based on whether the sequence is finished, scheduled, or in progress.
  getSequenceStatus(seq: Hss): string {
    if (seq.header.inProgress.text === "True") {
      return "EST";
    }

    if (seq.header.isComplete.text === "True") {
      return "GTR";
    }

    return "SKD";
  }

  getTafb(seq: Hss): HssItem {
    // Find last duty period by finding leg with sequence end flag.
    const debriefMinutes = seq.header.isInternational.text === "True" ? 30 : 15;
    let seqStartTime: DateTime | undefined;
    let seqEndTime: DateTime | undefined;
    let didChange = false;

    // Find the first duty period with at least one active leg.
    for (const dp of seq.dutyPeriods) {
      if (this.getActiveLegs(seq, dp.index) > 0) {
        seqStartTime = DateTime.fromFormat(dp.startTime.text, this.DATE_FORMAT);
        if (dp.startTime.didChange) {
          didChange = true;
        }
        break;
      }
    }

    // Need to use latest of last leg SKD/RSK/ACT. Using DP endtime is inaccurate in certain scenarios.
    for (const leg of seq.legs) {
      if (leg.isSeqEnd.text === "True") {
        const arrivalTimes: DateTime[] = [];

        if (leg.isArrTimeSched.text === "True") {
          arrivalTimes.push(
            DateTime.fromFormat(leg.arrTimeSched.text, this.DATE_FORMAT).plus({
              minutes: debriefMinutes,
            }),
          );
        }

        if (leg.isArrTimeReSched.text === "True") {
          arrivalTimes.push(
            DateTime.fromFormat(leg.arrTimeReSched.text, this.DATE_FORMAT).plus(
              { minutes: debriefMinutes },
            ),
          );
        }

        if (leg.isArrTimeActual.text === "True") {
          arrivalTimes.push(
            DateTime.fromFormat(leg.arrTimeActual.text, this.DATE_FORMAT).plus({
              minutes: debriefMinutes,
            }),
          );
        }

        seqEndTime = DateTime.max(...arrivalTimes);
        break;
      }
    }

    if (seqStartTime && seqEndTime) {
      return {
        text: seqEndTime.diff(seqStartTime).toFormat("h.mm"),
        property: "",
        didChange,
      };
    }

    return {
      text: "ERROR",
      property: "",
      didChange: false,
    };
  }

  // Parses text of format (h)h.mm into Luxon duration.
  private getDurationFromString(text: string): Duration {
    const pattern = /(?<hours>\d{1,2})\.(?<minutes>\d{2})/;

    if (!pattern.test(text)) {
      return Duration.fromObject({ minute: 0 });
    }

    const minutes = parseInt(text.match(pattern)?.groups?.["minutes"] || "0");
    const hours = parseInt(text.match(pattern)?.groups?.["hours"] || "0");

    return Duration.fromObject({ hours, minutes });
  }

  // Returns airline code if deadhead
  private getDhCode(leg: HssLeg): string {
    if (leg.isDhOrSurface.text === "True") {
      if (leg.oalSurfaceAirline.text !== "") {
        return leg.oalSurfaceAirline.text;
      } else {
        return "AA";
      }
    }

    return "";
  }

  // Find number of active legs for duty period.
  getActiveLegs(hss: Hss, dpIndex: number): number {
    let count = 0;

    for (const leg of hss.legs) {
      if (leg.isActive.text === "True" && leg.dpIndex === dpIndex) {
        count++;
      }
    }

    return count;
  }

  private getFlightTime(
    depTime: string,
    arrTime: string,
    leg: HssLeg,
    includeDelay = false,
  ): string {
    if (
      depTime.startsWith("01/01/0001") ||
      arrTime.startsWith("01/01/0001") ||
      depTime.trim() === "" ||
      arrTime.trim() === ""
    ) {
      return "";
    }

    let fltTime = DateTime.fromFormat(arrTime, this.DATE_FORMAT).diff(
      DateTime.fromFormat(depTime, this.DATE_FORMAT),
    );

    if (includeDelay) {
      // Add any ramp/atc/deice delay minutes.
      let rampMinutes = 0;
      let rampHours = 0;
      let atcMinutes = 0;
      let atcHours = 0;
      let deicingMinutes = 0;
      let deicingHours = 0;
      let diversionHours = 0;
      let diversionMinutes = 0;

      if (leg.rampDelayMinutes.text !== "") {
        rampHours = parseInt(leg.rampDelayMinutes.text.substring(0, 1));
        rampMinutes = parseInt(leg.rampDelayMinutes.text.substring(2));
      }

      if (leg.atcDelayMinutes.text !== "") {
        atcHours = parseInt(leg.atcDelayMinutes.text.substring(0, 1));
        atcMinutes = parseInt(leg.atcDelayMinutes.text.substring(2));
      }

      if (leg.deicingDelayMinutes.text !== "") {
        deicingHours = parseInt(leg.deicingDelayMinutes.text.substring(0, 1));
        deicingMinutes = parseInt(leg.deicingDelayMinutes.text.substring(2));
      }

      if (leg.diversionTime.text !== "") {
        diversionHours = parseInt(leg.diversionTime.text.substring(0, 1));
        diversionMinutes = parseInt(leg.diversionTime.text.substring(2));
      }

      fltTime = fltTime
        .plus({ hours: rampHours, minutes: rampMinutes })
        .plus({ hours: atcHours, minutes: atcMinutes })
        .plus({ hours: deicingHours, minutes: deicingMinutes })
        .plus({ hours: diversionHours, minutes: diversionMinutes });
    }

    return fltTime.toFormat("h.mm");
  }
}
